import { useRef, useState } from 'react';
import { Loader2, Upload, Video } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { uploadVideo } from '../../utils/api';

interface VideoUploadDropzoneProps {
  onUploaded: (url: string) => void;
  label?: string;
}

export default function VideoUploadDropzone({ onUploaded, label = 'Upload Video' }: VideoUploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const handlePickedFile = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please upload a video file.');
      return;
    }

    setIsUploading(true);
    const loadingToast = toast.loading('Uploading video...');
    try {
      const url = await uploadVideo(file);
      if (url) {
        onUploaded(url);
        toast.success('Video uploaded!', { id: loadingToast });
      } else {
        toast.error('Failed to upload video.', { id: loadingToast });
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className={`rounded-xl border-2 border-dashed p-4 transition-all ${isDragOver ? 'border-[#722F38] bg-[#722F38]/5' : 'border-gray-200 bg-gray-50/50'}`}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isUploading) setIsDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (isUploading) return;
        handlePickedFile(e.dataTransfer.files?.[0]);
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Video className="w-4 h-4 text-gray-400" />
          <span>Drag & drop video or click upload</span>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#722F38] text-white text-xs font-bold cursor-pointer hover:bg-[#5a252d] transition-colors disabled:opacity-60"
        >
          {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1.5" />{label}</>}
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/webm,video/ogg"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.currentTarget.value = '';
          handlePickedFile(file);
        }}
      />
      <p className="text-[10px] text-gray-400 italic mt-2">MP4/WebM/Ogg. Served from your `/uploads`.</p>
    </div>
  );
}
